import { createApp, provide } from "vue";
import TurbolinksAdapter from "vue-turbolinks";
import { MotionPlugin } from "@vueuse/motion";
// import Button from "@components/Button.vue";
// import Icon from "@components/Icon.vue";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { library } from "@fortawesome/fontawesome-svg-core";
import { faEyeSlash, faEye } from "@fortawesome/free-regular-svg-icons";
// _Regular_ FontAwesome icons should be referenced with a prefix.
// For example: `:icon="['far','eye']"`
import {
  faPlay,
  faStop,
  faStopCircle,
  faPause,
  faCaretLeft,
  faCaretRight,
  faStepBackward,
  faStepForward,
  faRotateLeft,
  faBackward,
  faForward,
  faChevronLeft,
  faVolumeUp,
  faVolumeMute,
  faExpand,
  faCompress,
  faClosedCaptioning,
} from "@fortawesome/free-solid-svg-icons";
// _Solid_ FontAwesome icons don’t need a prefix, so like: `icon="pause"`

import Appsignal from "@appsignal/javascript";
import { useAppsignal } from "@modules/appsignal";
import { plugin as windowEventsPlugin } from "@appsignal/plugin-window-events";
import { plugin as pathDecoratorPlugin } from "@appsignal/plugin-path-decorator";
import { plugin as networkBreadcrumbsPlugin } from "@appsignal/plugin-breadcrumbs-network";

import { createStore } from "vuex";
import VuexORM from "@vuex-orm/core";
import VuexORMAxios from "@vuex-orm/plugin-axios";
import applyConverters from "axios-case-converter";
import axios from "axios";

import PublicApp from "@public/PublicApp.vue";

import Video from "@models/Video";
import Recording from "@models/Recording";
import VoiceOver from "@models/VoiceOver";
import AudioRecording from "@models/AudioRecording";
import Upload from "@models/Upload";

import router from "@public/public_router";
import {
  Action,
  BrowserAction,
  Click,
  Input,
  Keypress,
  Pause,
  Overlay,
  Slide,
  TextSlide,
  VideoSlide,
  ImageSlide,
} from "../models/ActionHierarchy";

// import "@assets/stylesheets/frontend.scss";
import "@assets/stylesheets/public.scss";

VuexORM.use(VuexORMAxios, {
  axios: applyConverters(axios.create()),
  baseURL: "/api/v1/",
});

// Create a new instance of Database.
const database = new VuexORM.Database();

// Register Models to Database.
database.register(Video);
database.register(Recording);
database.register(VoiceOver);
database.register(AudioRecording);
database.register(Upload);

database.register(Action);
database.register(BrowserAction);
database.register(Click);
database.register(Input);
database.register(Keypress);
database.register(Pause);
database.register(Overlay);
database.register(Slide);
database.register(TextSlide);
database.register(VideoSlide);
database.register(ImageSlide);

const collectionsPersistedToDisk = ["preferences"].map(
  (collection) => `entities.${collection}.data`,
);

// Create Vuex Store and register database through Vuex ORM.
const store = createStore({
  plugins: [VuexORM.install(database)],
}); // For ES Module

const appsignalFrontEndKeys = {
  production: "0e7c6b8c-796e-4e13-9368-4f5b5ca0be85",
  production_debug: "2556f7fe-17aa-47d6-b63e-9ec9b2c6981e",
  development: "2a51a8d7-8149-4a0b-abb3-481880ae7914",
};
const appsignalFrontEndKey =
  appsignalFrontEndKeys[document.querySelector<HTMLElement>(":root")?.dataset.environment || ""];

const appsignal = new Appsignal({
  key: appsignalFrontEndKey,
  revision: document.body.dataset.revision,
});
appsignal.use(windowEventsPlugin());
appsignal.use(pathDecoratorPlugin());
appsignal.use(networkBreadcrumbsPlugin());

const { errorHandler } = useAppsignal(appsignal);

library.add(
  faEye,
  faEyeSlash,
  faPause,
  faPlay,
  faStop,
  faStopCircle,
  faCaretLeft,
  faCaretRight,
  faStepBackward,
  faStepForward,
  faRotateLeft,
  faBackward,
  faForward,
  faChevronLeft,
  faVolumeUp,
  faVolumeMute,
  faExpand,
  faCompress,
  faClosedCaptioning,
);

document.addEventListener("DOMContentLoaded", () => {
  const params = new URL(document.location.href).searchParams;

  const props = {
    // videoToken: document.body.dataset.videoToken,
    headless: Boolean(params.get("headless")),
    autoplay: Boolean(params.get("autoplay")),
    loop: Boolean(params.get("loop")),
    replayCanvas: document.body.dataset.replayCanvas === "true",
  };
  // console.log("initializing app with props", props);
  const app = createApp(PublicApp, props);
  // app.component("Button", Button);
  // app.component("Icon", Icon);
  app.component("FontAwesomeIcon", FontAwesomeIcon);
  app.provide("appsignal", appsignal);
  app.use(router);
  app.use(store);
  app.use(TurbolinksAdapter);
  app.use(MotionPlugin);
  app.config.errorHandler = errorHandler(app);
  app.config.unwrapInjectedRef = true; // default in vue 3.3
  app.mount(document.body);
});
